import cn from 'clsx'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import SolidCircleIcon from 'LuxKit/Icons/solid/SolidCircleIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Typography from 'LuxKit/Typography/Typography'
import { diffInDays } from 'lib/datetime/dateUtils'
import noop from 'lib/function/noop'
import { pluralizeToString } from 'lib/string/pluralize'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'

const Cell = styled(Clickable)`
  position: relative;
  min-height: ${rem(60)};
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  padding: ${rem(4)} ${rem(2)} 0 ${rem(2)};
  background-color: ${props => props.theme.palette.neutral.default.eight};

  &:active {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.type-check-in, &.type-check-out {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.type-between {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &.type-none.variant-unavailable {
    color: ${props => props.theme.palette.neutral.default.four};

    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.default.eight};
      }
    }
    &:active {
      background-color: ${props => props.theme.palette.neutral.default.eight};
    }
  }
`

const DurationLabel = styled(StructureLabel)`
  position: absolute;
  right: -${rem(20)};
  top: ${rem(6)};
  z-index: 1;
  transform: translateY(-100%);
`

const FeaturedIcon = styled(SolidFireIcon)`
  position: absolute;
  top: 0;
  right: ${rem(2)};
`

const Content = styled(Typography)`
  opacity: 0;

  &.visible {
    opacity: 1;
  }
`

export type OfferCalendarSidebarDayCellType = 'check-in' | 'check-out' | 'between' | 'none'
export type OfferCalendarSidebarDayCellVariant = 'price-member' | 'price-cheapest' | 'price' | 'none' | 'sold-out-rooms' | 'sold-out-recommendations' | 'sold-out-hotel' | 'unavailable'
export type OfferCalendarSidebarDayCellFeatured = 'special' | 'loyalty'

interface Props {
  /** The raw calendar data for a day */
  day: App.CalendarDay
  /**
   * Whether or not we have flights on or not
   */
  flights?: boolean
  /**
   * Event for when the cell is clicked, provides the day object to the listener
   */
  onClick?: (day: App.CalendarDay) => void
  /**
   * The purpose the cell is for, determines which components
   * in the cell are shown
   */
  variant: OfferCalendarSidebarDayCellVariant
  /**
   * The 'state' the cell is currently in
   * Determines mostly how the *cell* looks
   */
  type: OfferCalendarSidebarDayCellType
  /**
   * Whether or not this is a featured "day"
   * Will apply the appropriate icon to make it look significant
   */
  featured?: OfferCalendarSidebarDayCellFeatured
  /**
   * The price to display for the cell
   */
  price?: number
  style?: React.CSSProperties
}

function OfferCalendarSidebarDayCell(props: Props) {
  const { day, flights, onClick = noop, variant, type, featured, price = 0, style } = props

  const priceColour = useMemo<React.ComponentProps<typeof Caption>['colour']>(() => {
    if (variant === 'price-member') {
      return 'lux-plus'
    } else if (variant === 'price-cheapest' && type !== 'check-in') {
      return 'highlight-secondary'
    }
    return 'neutral-two'
  }, [variant, type])

  const showContent = type !== 'between' && type !== 'check-out'
  const duration = diffInDays(new Date(day.checkIn), new Date(day.checkOut))

  const soldOutText = useMemo(() => {
    switch (variant) {
      case 'sold-out-hotel':
        return <>Hotel<br />only</>
      case 'sold-out-rooms':
        return <>View<br /> rooms</>
      case 'sold-out-recommendations':
        return <>More<br /> offers</>
      default:
        return undefined
    }
  }, [variant])

  const [tier] = useCurrentLoyaltyTier()

  return <Cell
    style={style}
    onClick={() => onClick(day)}
    className={cn(
      `type-${type}`,
      `variant-${variant}`,
    )}
  >
    {featured === 'special' && type !== 'between' && <FeaturedIcon colour="urgency" size="XXS" />}
    {featured === 'loyalty' && type !== 'between' && <FeaturedIcon as={SolidCircleIcon} size="XXS" colour={`tier-${tier}`} />}
    {type === 'check-out' && <DurationLabel variant="dark">
      {pluralizeToString('night', duration)}
    </DurationLabel>}
    <BodyText variant="medium" align="center">{day.day}</BodyText>
    <Content align="center" className={cn({ visible: showContent })}>
      {(variant === 'price' || variant === 'price-cheapest' || variant === 'price-member') && <Caption
        variant="smallalt"
        tabletVariant="medium"
        colour={priceColour}
      >
        <FormatCurrency
          value={price}
          format="dynamic"
          notation={price > 10000 ? 'compact' : undefined}
          maximumFractionDigits={price > 10000 ? 1 : undefined}
        />
        {flights ? '*' : ''}
      </Caption>}
      {!!soldOutText && <Caption variant="smallalt" tabletVariant="medium" colour="neutral-three" underline>
        {soldOutText}
      </Caption>}
    </Content>
  </Cell>
}

export default React.memo(OfferCalendarSidebarDayCell)
